import ApiService from './api.service';

// Mirrors Linear's workflow-state types (Linear is the source of truth for status).
export type TaskStatus =
  | 'triage'
  | 'backlog'
  | 'unstarted'
  | 'started'
  | 'completed'
  | 'canceled';
export type OrgResponsibility = 'PLIABLE' | 'TENANT' | 'END_USER';

export type Task = {
  id: string;
  created_by?: string | null;
  created_at: string;
  thread_id?: string | null;
  agent_type?: string | null;
  task_type: string;
  title: string;
  description: string;
  context?: string | null;
  org_responsibility: OrgResponsibility;
  owner?: string | null;
  status: TaskStatus;
  resolved_at?: string | null;
  resolved_by?: string | null;
  status_notes?: string | null;
  source_video_source_id?: string | null;
  source_clip_id?: string | null;
  category?: string | null;
  linear_issue_id?: string | null;
  linear_issue_uuid?: string | null;
};

/** Absolute URL to the Linear issue (the workspace slug is stable: "pliable"). */
export function linearIssueUrl(identifier: string): string {
  return `https://linear.app/pliable/issue/${identifier}`;
}

/**
 * Deep link that opens jimi-desktop and starts a new task in the tenant's client
 * project, seeded from the Linear issue. Canary and stable jimi builds register distinct
 * schemes so they coexist — dev-beta is paired with canary — and stack + tenant let jimi
 * resolve the right local project (same rule as unparkDeepLink).
 */
export function startTaskDeepLink(
  stack: string,
  tenantSlug: string,
  issueIdentifier: string,
  title: string,
): string {
  const scheme = stack === 'dev-beta' ? 'jimi-canary' : 'jimi';
  // Carry just enough to pre-link the issue in jimi's create-task modal (identifier in the
  // path; title + url as params). jimi builds the LinkedIssue and pops the modal.
  const params = new URLSearchParams({
    stack,
    tenant: tenantSlug,
    title: title.slice(0, 300),
    url: linearIssueUrl(issueIdentifier),
  });
  return `${scheme}://start-task/${encodeURIComponent(issueIdentifier)}?${params.toString()}`;
}

export type CreateTaskPayload = {
  task_type: string;
  title: string;
  description: string;
  context?: string | null;
  thread_id?: string | null;
  agent_type?: string | null;
  org_responsibility?: OrgResponsibility;
};

export type ListTasksParams = {
  page?: number;
  pageSize?: number;
  status?: TaskStatus | 'open' | 'all';
  owner?: string | null;
  taskType?: string;
  orgResponsibility?: OrgResponsibility;
  search?: string;
  sort?: string;
};

export type ListTasksResult = {
  records: Task[];
  total_records: number;
  total_pages: number;
};

// A comment on the task's linked Linear issue (read-only mirror).
export type LinearComment = {
  id: string;
  body: string;
  url?: string | null;
  createdAt?: string | null;
  user?: { name?: string | null; email?: string | null; displayName?: string | null } | null;
};

export class TaskService {
  async create(payload: CreateTaskPayload): Promise<Task> {
    const result = (await ApiService.getInstance().request(
      'POST',
      '/tasks',
      payload,
    )) as { record: Task };
    return result.record;
  }

  async list(params?: ListTasksParams): Promise<ListTasksResult> {
    const {
      page = 1,
      pageSize = 25,
      status,
      owner,
      taskType,
      orgResponsibility,
      search,
      sort = '-created_at',
    } = params || {};

    const mongoFilter: Record<string, any> = {};
    if (status === 'open') {
      mongoFilter.status = { $nin: ['completed', 'canceled'] };
    } else if (status && status !== 'all') {
      mongoFilter.status = status;
    }
    if (owner !== undefined && owner !== null && owner !== '') {
      mongoFilter.owner = owner;
    } else if (owner === null) {
      mongoFilter.owner = { $in: [null, ''] };
    }
    if (taskType) mongoFilter.task_type = taskType;
    if (orgResponsibility) mongoFilter.org_responsibility = orgResponsibility;
    if (search && search.trim()) {
      mongoFilter.title = { $regex: search.trim(), $options: 'i' };
    }

    const query: Record<string, any> = {
      page,
      limit: pageSize,
      sort,
    };
    if (Object.keys(mongoFilter).length > 0) {
      query.filter = JSON.stringify(mongoFilter);
    }

    return (await ApiService.getInstance().request(
      'GET',
      '/tasks',
      query,
    )) as ListTasksResult;
  }

  async update(id: string, patch: Partial<Task>): Promise<Task> {
    const result = (await ApiService.getInstance().request(
      'PATCH',
      `/tasks/${id}`,
      patch,
    )) as { record: Task };
    return result.record;
  }

  // Status change: stamps resolved_at/resolved_by on the backend and pushes the
  // matching workflow state to the linked Linear issue.
  async setStatus(id: string, status: TaskStatus, statusNotes?: string | null): Promise<Task> {
    const result = (await ApiService.getInstance().request('PATCH', `/tasks/${id}/status`, {
      status,
      status_notes: statusNotes ?? null,
    })) as { record: Task };
    return result.record;
  }

  // Assignee change: stores `owner`; `email` is used to match a Linear user and push
  // the assignee (no-op in Linear if no match).
  async setAssignee(
    id: string,
    owner: string | null,
    email?: string | null,
  ): Promise<Task> {
    const result = (await ApiService.getInstance().request('PATCH', `/tasks/${id}/assignee`, {
      owner,
      email: email ?? null,
    })) as { record: Task };
    return result.record;
  }

  async addComment(id: string, body: string): Promise<LinearComment | null> {
    const result = (await ApiService.getInstance().request('POST', `/tasks/${id}/comment`, {
      body,
    })) as { comment: LinearComment | null };
    return result.comment;
  }

  async getComments(id: string): Promise<LinearComment[]> {
    const result = (await ApiService.getInstance().request(
      'GET',
      `/tasks/${id}/comments`,
    )) as { comments: LinearComment[] };
    return result.comments ?? [];
  }
}

export const taskService = new TaskService();
